import { z } from "zod";

/** Lifecycle phase of a hedge pair. */
export const PairPhaseZ = z.enum(["compound", "harvest", "pivot"]);
export type PairPhase = z.infer<typeof PairPhaseZ>;

export const PairStatusZ = z.enum(["setup", "active", "closed"]);
export type PairStatus = z.infer<typeof PairStatusZ>;

/**
 * AI involvement level for a pair. Orthogonal to status — a pair must
 * be `status='active'` for any of these to take effect.
 *   manual     -> Haiku heartbeat suppressed; operator owns everything.
 *   semi_auto  -> Haiku runs; entry/exit decisions queue in
 *                 `pair_suggestions` for operator approval; safety
 *                 reflexes (news_protect/restore, force_close_all)
 *                 still auto-execute.
 *   full_auto  -> Haiku autonomously executes every decision.
 */
export const PairModeZ = z.enum(["manual", "semi_auto", "full_auto"]);
export type PairMode = z.infer<typeof PairModeZ>;

/**
 * A hedge pair: two accounts (long + short) on the same firm, same symbol,
 * same lot size. One side is guaranteed to pass each phase; the loser is
 * allowed to blow up once max drawdown is hit.
 */
export const PairZ = z.object({
  id: z.string().min(1).max(32),
  firmId: z.string().min(1).max(32),
  longAccount: z.string().min(1).max(32),
  shortAccount: z.string().min(1).max(32),
  symbol: z.string().min(1).max(16),
  lots: z.number().positive(),
  /** Contract size (units per lot) for this symbol, captured at
   *  validate time. Passed down to the bot in trade:prepare so it can
   *  compute sustainable lots from freeMargin × leverage ÷
   *  (contractSize × price). Null when the driver couldn't scrape the
   *  spec — auto-sizing falls back to target lots. */
  contractSize: z.number().positive().nullable().optional(),
  /** Account leverage ratio (e.g. 100 for 1:100) for the pair. Same
   *  fallback semantics as contractSize. */
  leverage: z.number().positive().nullable().optional(),
  phase: PairPhaseZ.default("compound"),
  status: PairStatusZ.default("setup"),
  mode: PairModeZ.default("manual"),
  createdAt: z.number().int(),
  pivotedAt: z.number().int().nullable(),
});
export type Pair = z.infer<typeof PairZ>;

/** Hot pair state pushed to the dashboard via WS. */
export const PairStateZ = z.object({
  pairId: z.string(),
  firmId: z.string(),
  symbol: z.string(),
  phase: PairPhaseZ,
  status: PairStatusZ,
  mode: PairModeZ,
  netPnl: z.number().nullable(),
  longEquity: z.number().nullable(),
  shortEquity: z.number().nullable(),
  longDrawdownUsedPct: z.number().nullable(),
  shortDrawdownUsedPct: z.number().nullable(),
  longDailyUsedPct: z.number().nullable(),
  shortDailyUsedPct: z.number().nullable(),
  updatedAt: z.number().int(),
});
export type PairState = z.infer<typeof PairStateZ>;

/**
 * Per-account rule snapshot. Every field nullable because rules can be
 * unconfirmed OR partial (a niche firm may not publish consistency-rule
 * numbers). Claude prompts always render the shape; a NULL reads as
 * "use the firm default from RAG firm-rules."
 */
export const AccountRuleSnapshotZ = z.object({
  accountId: z.string(),
  firmId: z.string(),
  accountSize: z.number().int().nullable(),
  steps: z.number().int().nullable(),
  challengeType: z.string().nullable(),
  dailyLossPct: z.number().nullable(),
  dailyDdType: z.string().nullable(),
  maxDrawdownPct: z.number().nullable(),
  maxDdType: z.string().nullable(),
  profitTargetPct: z.number().nullable(),
  profitSplitPct: z.number().nullable(),
  payoutFrequency: z.string().nullable(),
  leverage: z.string().nullable(),
  minTradingDays: z.number().int().nullable(),
  weekendHoldingAllowed: z.boolean().nullable(),
  newsTradingAllowed: z.boolean().nullable(),
  consistencyRulePct: z.number().nullable(),
  rulesConfirmed: z.boolean(),
});
export type AccountRuleSnapshot = z.infer<typeof AccountRuleSnapshotZ>;

/**
 * Today's trading window for this pair. Picked fresh at daily UTC 00:00
 * rollover with crypto-random jitter (8-10 AM UTC start, 7-8 PM UTC end).
 * `mode: "weekend"` disables all opens + forces close if positions remain.
 */
export const TradingWindowZ = z.object({
  day: z.string(),
  mode: z.enum(["trade", "weekend"]),
  windowStart: z.number().int().nullable(),
  windowEnd: z.number().int().nullable(),
  forceCloseAt: z.number().int().nullable(),
  nowUtc: z.number().int(),
  isWeekend: z.boolean(),
});
export type TradingWindow = z.infer<typeof TradingWindowZ>;

/** One open position snapshot shipped to Haiku in the user message. */
export const OpenPositionSnapshotZ = z.object({
  side: z.enum(["long", "short"]),
  accountId: z.string(),
  ticket: z.number().int().nullable(),
  lots: z.number().nullable(),
  entry: z.number().nullable(),
  sl: z.number().nullable(),
  tp: z.number().nullable(),
  pnlOpen: z.number().nullable(),
  updatedAt: z.number().int().nullable(),
  /** True if the position is currently in news-protect state (TP removed,
   *  SL pulled to daily-DD-edge). Drives Haiku's news_restore decision. */
  newsProtected: z.boolean().default(false),
});
export type OpenPositionSnapshot = z.infer<typeof OpenPositionSnapshotZ>;

/**
 * Context bundle the Worker assembles before calling Claude. Mirrors the
 * shape of `PairContext` inside the Worker but shared here so both sides
 * can safely serialize it across WS/queue boundaries.
 */
export const PairContextZ = z.object({
  pairId: z.string(),
  firmId: z.string(),
  symbol: z.string(),
  baseCurrency: z.string().length(3),
  phase: PairPhaseZ,
  /** AI involvement level. Haiku reads this to know when its
   *  open_trade / close_pair decisions will be queued for operator
   *  approval (semi_auto) vs auto-executed (full_auto). */
  mode: PairModeZ.default("full_auto"),
  longAccount: z.string(),
  shortAccount: z.string(),
  state: PairStateZ.pick({
    longDrawdownUsedPct: true,
    shortDrawdownUsedPct: true,
    longDailyUsedPct: true,
    shortDailyUsedPct: true,
    netPnl: true,
  }),
  /** Operator-acknowledged per-account rules for both legs. Claude
   *  prompts include these BEFORE the firm-rules RAG chunks and must
   *  prefer them on conflict. Optional for backwards compatibility:
   *  a missing value means "use RAG firm defaults." */
  longRules: AccountRuleSnapshotZ.nullable().optional(),
  shortRules: AccountRuleSnapshotZ.nullable().optional(),
  /** Today's UTC trading window + weekend flag. Null during legacy call
   *  paths that haven't migrated to the windowed scheduler. */
  tradingWindow: TradingWindowZ.nullable().optional(),
  /** Active positions per side (0..2 entries). Empty array when both
   *  sides are flat. */
  openPositions: z.array(OpenPositionSnapshotZ).optional(),
  lastDecision: z.unknown().nullable(),
  /** True only when BOTH legs' bots currently have a live WebSocket
   *  to BotChannel. Pre-flight for open_trade — Haiku refuses to open
   *  when either side is dark so we don't waste a 10s handshake. */
  bothBotsOnline: z.boolean().default(false),
  /** Operator-defined allowlist of symbols Haiku is permitted to open on
   *  this account pair. Empty array = nothing allowed (account pair
   *  configured but not yet enabled). If the current `symbol` is NOT in
   *  this list the coordinator rejects open_trade as
   *  `symbol_not_in_allowlist`. */
  symbolAllowlist: z.array(z.string()).default([]),
});
export type PairContext = z.infer<typeof PairContextZ>;
